import { resolverAlvo } from "./alvo";

/* ============================================================================
   Tour do professor — ESPERA POR ALVO
   ============================================================================
   As telas da jornada carregam por axios. Quando a rota monta, o alvo do passo
   seguinte quase nunca está no DOM: a listagem de atividades só tem o botão
   "Novo" e a linha 0 da tabela depois que a request volta.

   Sem esta espera, o tour aponta para o vazio e para de andar — é a falha mais
   comum em tour ancorado, e a razão de o timeout aqui ser obrigatório e não
   opcional. Não achou em `timeoutMs`? O passo é PULADO. Um passo perdido é um
   incômodo; um tour paralisado é um chamado no suporte.

   A espera resolve por EXISTÊNCIA, não por visibilidade: item dentro de dropdown
   fechado conta, porque o orquestrador sabe abrir o dropdown depois (ver
   `abrirDropdowns` em ./alvo.js). Exigir visibilidade aqui era o que fazia
   Conteúdos e Avaliações ADR nunca aparecerem.
   ========================================================================== */

/**
 * Resolve quando o alvo do passo aparecer no DOM, ou `null` no timeout.
 *
 * Usa MutationObserver em vez de polling porque o custo é por mutação e não por
 * intervalo — e estas telas montam árvores grandes de uma vez, quando a request
 * volta.
 *
 * A verificação síncrona antes de observar não é otimização: o alvo pode JÁ
 * estar lá (passo na mesma tela, sem navegação), e nesse caso nenhuma mutação
 * viria e a promessa só resolveria no timeout.
 *
 * @param {object|string} passo      o passo, ou só um seletor (conveniência)
 * @param {number}        timeoutMs
 * @param {Document}      [doc]      injetável para teste
 * @returns {Promise<Element|null>}
 */
export default function aguardarAlvo(passo, timeoutMs, doc = document) {
  const alvo = typeof passo === "string" ? { alvo: passo } : passo;

  const jaEsta = resolverAlvo(alvo, doc);
  if (jaEsta) return Promise.resolve(jaEsta);

  return new Promise(resolve => {
    let observer = null;
    let timer = null;

    const encerrar = resultado => {
      if (observer) observer.disconnect();
      if (timer) clearTimeout(timer);
      observer = null;
      timer = null;
      resolve(resultado);
    };

    timer = setTimeout(() => encerrar(null), timeoutMs);

    // Ambiente sem MutationObserver (jsdom antigo): resta o timeout, e o passo
    // é pulado em vez de travar.
    if (typeof MutationObserver === "undefined") return;

    observer = new MutationObserver(() => {
      const encontrado = resolverAlvo(alvo, doc);
      if (encontrado) encerrar(encontrado);
    });

    observer.observe(doc.body, { childList: true, subtree: true });
  });
}

/** Intervalo e teto do polling de visibilidade, em quadros de ~30ms. */
const PASSO_POLL_MS = 30;
const TENTATIVAS_VISIVEL = 20; // ~600ms

/**
 * Espera um elemento JÁ ENCONTRADO ficar visível, depois de abrirmos o dropdown
 * que o continha.
 *
 * Existe separado de `aguardarAlvo` porque aqui não se procura nada: o nó é o
 * mesmo, só falta o CoreUI aplicar a classe `c-show` num re-render do React.
 * Bastam alguns quadros — daí o polling curto por CONTAGEM de tentativas, e não
 * por relógio: fica determinístico no teste, sem precisar controlar o tempo.
 *
 * @returns {Promise<boolean>} ficou visível?
 */
export function aguardarVisivel(
  elemento,
  ehVisivel,
  tentativas = TENTATIVAS_VISIVEL
) {
  if (ehVisivel(elemento)) return Promise.resolve(true);

  return new Promise(resolve => {
    let restantes = tentativas;

    const tentar = () => {
      if (ehVisivel(elemento)) {
        resolve(true);
        return;
      }

      restantes -= 1;
      if (restantes <= 0) {
        resolve(false);
        return;
      }

      setTimeout(tentar, PASSO_POLL_MS);
    };

    setTimeout(tentar, PASSO_POLL_MS);
  });
}
